import { Moment } from 'moment';
import { PageRequest } from '@/core/request/PageRequest';
import { InvoiceDocReferencedPayload } from '../referencedDocument/referencedDocument.types';
import {
  InvoiceDocumentType,
  InvoiceEmissionGoal,
  InvoiceEmissionType,
  InvoiceOperationDestination,
  InvoiceOperationType,
  InvoiceOriginReferenceEntity,
  InvoiceRecipientType,
  InvoiceSituation,
  InvoiceSpedStatusRegistered,
  InvoiceTaxRegime,
  InvoiceTaxationNature,
  InvoiceTaxpayerType,
  InvoiceType,
} from '../invoices/invoices.enums';
import { InvoiceOriginReference } from '../invoices/invoices.types';
import { CustomerCategory } from '../../people/CustomerCategory/CustomerCategory.types';
import {
  InvoiceCreditNoteType,
  InvoiceDebitNoteType,
  InvoiceGovernmentOperation,
  InvoiceGovernmentType,
} from './invoice.enums';

export enum DocumentType {
  entry = 'ENTRY',
  exit = 'EXIT',
}

export enum OperationDestination {
  internalOperation = 'INTERNAL_OPERATION',
  interstateOperation = 'INTERSTATE_OPERATION',
  foreignOperation = 'FOREIGN_OPERATION',
}

export enum EmissionType {
  normalEmission = 'NORMAL_EMISSION',
  contingencyFsIa = 'CONTINGENCY_FS_IA',
  contingencyDpec = 'CONTINGENCY_DPEC',
  contingencyFsDa = 'CONTINGENCY_FS_DA',
  contingencySvcAn = 'CONTINGENCY_SVC_AN',
  contingencySvcRs = 'CONTINGENCY_SVC_RS',
}

export enum PresenceIndications {
  notApplicable = 'NOT_APPLICABLE',
  faceToFaceOperation = 'FACE_TO_FACE_OPERATION',
  nonFaceToFaceOperationOverTheInternet = 'NON_FACE_TO_FACE_OPERATION_OVER_THE_INTERNET',
  nonFaceToFaceOperationCallCenter = 'NON_FACE_TO_FACE_OPERATION_CALLCENTER',
  faceToFaceOperationOffSite = 'FACE_TO_FACE_OPERATION_OFF_SITE',
  nonFaceToFaceOperationOthers = 'NON_FACE_TO_FACE_OPERATION_OTHERS',
}

export enum DestinationNfse {
  taxationMunicipality = 'TAXATION_MUNICIPALITY',
  taxationOutsideMunicipality = 'TAXATION_OUTSIDE_MUNICIPALITY',
  immunity = 'IMMUNITY',
  immune = 'IMMUNE',
  demandSuspendedCourtDecision = 'DEMAND_SUSPENDED_COURT_DECISION',
  demandSuspendedAdministrativeProcedure = 'DEMAND_SUSPENDED_ADMINISTRATIVE_PROCEDURE',
}

export const InvoiceSituationColor = {
  [InvoiceSituation.REPLACED]: 'purple',
  [InvoiceSituation.IN_TYPING]: 'gray',
  [InvoiceSituation.VALIDATED]: 'cyan',
  [InvoiceSituation.PROCESSING]: 'blue',
  [InvoiceSituation.AUTHORIZED]: 'green',
  [InvoiceSituation.CANCELED]: 'red',
  [InvoiceSituation.PROCESSING_CANCELLATION]: 'red',
  [InvoiceSituation.DENIED]: 'red',
  [InvoiceSituation.INTERRUPTED]: 'orange',
};

export const ReferentialTranslator = {
  [InvoiceOriginReferenceEntity.PURCHASE]: 'Compra',
  [InvoiceOriginReferenceEntity.SALES]: 'Venda Direta',
  [InvoiceOriginReferenceEntity.WORK_ORDER]: 'O.S.',
};

export enum InvoiceSituationEnum {
  REPLACED = 'Substituído',
  IN_TYPING = 'Em Digitação',
  VALIDATED = 'Validada',
  PROCESSING = 'Em Processamento',
  AUTHORIZED = 'Autorizada',
  CANCELED = 'Cancelada',
}

export enum DeliveryType {
  EMITTER = 'EMITTER',
  RECIPIENT = 'RECIPIENT',
  THIRD_PARTY = 'THIRD_PARTY',
  SENDER_OWN_TRANSPORT = 'SENDER_OWN_TRANSPORT',
  RECIPIENT_OWN_TRANSPORT = 'RECIPIENT_OWN_TRANSPORT',
  NO_SHIPPING = 'NO_SHIPPING',
}

export enum TaxRegime {
  normalRegime = 'NORMAL_REGIME',
  simpleNational = 'SIMPLE_NATIONAL',
  simpleNationalExcessSublimitGrossRevenue = 'SIMPLE_NATIONAL_EXCESS_SUBLIMIT_GROSS_REVENUE',
}

export enum RecipientType {
  customer = 'CUSTOMER',
  supplier = 'SUPPLIER',
  insurance = 'INSURANCE',
}

export enum TypeOfTaxpayers {
  icmsTaxPayer = 'ICMS_TAXPAYER',
  exemptTaxPayer = 'EXEMPT_TAXPAYER',
  nonContributor = 'NON_CONTRIBUTOR',
}

export enum ProcessedByIntermediary {
  yes = 'YES',
  no = 'NO',
}

export enum FinalCostumer {
  yes = 'YES',
  no = 'NO',
}

export enum SelectNoteType {
  nfe = 'NFE',
  nfce = 'NFCE',
  nfse = 'NFSE',
  cfe = 'CFE',
}

export enum IntermediaryIndicationType {
  cpf = 'CPF',
  cnpj = 'CNPJ',
}

export interface Invoice {
  aliquotCofins: number;
  aliquotIcms: number;
  aliquotIpi: number;
  aliquotPis: number;
  calcPercentCofins: boolean;
  cest: string;
  code: number;
  csosnIcms: string;
  cstCofins: string;
  cstCofinsEntry: string;
  cstIcms: string;
  cstIcmsEntry: string;
  cstIpi: string;
  cstIpiEntry: string;
  cstPis: string;
  cstPisEntry: string;
  description: string;
  id: number;
  modalityDerteminationBcIcms: number;
  mva: number;
  reductionIcms: number;
  taxesPercentage: number;
}

export interface ListInvoicesQuery extends PageRequest {
  number?: number;
  serie?: string;
  emissionDate?: string;
  recipient?: string;
  situation?: InvoiceSituation;
  companyId?: number;
}

export interface InvoiceNFeRecipientFormValues {
  selectNoteType: SelectNoteType;
  recipientType: InvoiceRecipientType;
  cpfCnpj: string;
  foreignDocument?: string;
  customer?: string;
  customerId: number;
  supplier?: string;
  supplierId: number;
  insurer?: string;
  insurerId: number;
  taxpayerType: InvoiceTaxpayerType;
  intermediaryCNPJ: number;
  SellerId: string;
  registrationIdentifier: string;
  intermediaryIndicationType?: IntermediaryIndicationType;
  intermediaryCityCode?: string;
  intermediaryCompanyName?: string;
  intermediaryCpfCnpj?: string;
  intermediaryMunicipalRegistration?: string;
  fantasyName?: string;
  customerAddress?: string;
  cityName?: string;
  customerCategory?: CustomerCategory;
  publicAgency?: string;
}

export enum NatureOfOperationType {
  taxationMunicipality = 'taxationMunicipality',
  taxationoutsideMunicipality = 'taxationoutsideMunicipality',
  exemption = 'exemption',
  immune = 'immune',
  suspendedCourtOrder = 'suspendedCourtOrder',
  suspendedAdministrativeProcedure = 'suspendedAdministrativeProcedure',
}

export interface InvoiceResponseError {
  error: { message: string; data: Record<string, string> };
}

export interface InvoiceGeneralFormValues {
  // TODO: Refatorar as props para o formato da API e RENOMAR AS INTERFACES DE TIPOS PARA OS NOMES CORRETOS
  invoiceType: InvoiceType;
  companyId: number;
  docReferenceds: InvoiceDocReferencedPayload[];
  cityId: number;
  cityName: string;
  model: string;
  serie: string;
  number: string;
  key: string;
  destination: string;
  emissionGoal: InvoiceEmissionGoal;
  debitType?: InvoiceDebitNoteType;
  creditType?: InvoiceCreditNoteType;
  situation: InvoiceSituation;
  emissionDate: Moment;
  documentType: InvoiceDocumentType;
  finalCostumer: FinalCostumer;
  operationDestination: InvoiceOperationDestination;
  entryExitDate: string;
  emissionType: InvoiceEmissionType;
  presenceIndication: PresenceIndications;
  brokerIndicator: string;
  includeIcms: boolean;
  includeIpi: boolean;
  recipient: InvoiceNFeRecipientFormValues;
  departureDate: Moment;
  taxpayerType: InvoiceTaxpayerType;
  intermediaryIndication: string;
  intermediaryIndicationNfs: boolean;
  natureOfOperation: string;
  finalConsumer: string;
  customerId?: number;
  supplierId?: number;
  insurerId?: number;
  simpleNational?: string;
  culturalIncentivator?: string;
  issWithheld?: string;
  deductIss?: string;
  pisCofinsWithholding?: string;
  retentionPisCofins?: boolean;
  publicAgency?: string;
  // --------NFSe----------
  numberRps: string;
  intermediaryCpfCnpj: string;
  taxRegime?: InvoiceTaxRegime;
  registrationIdentifier?: string;
  faceToFace: string;
  intermediaryCityCode: string;
  description: string;
  integrationId: string;
  taxCode?: string;
  protocol?: string;
  cancellationProtocol?: string;
  responsibleForRetention?: boolean;
  cpfCnpjType?: string;
  isIntermediaryIndication?: boolean;
  intermediaryCompanyName?: string;
  intermediaryMunicipalRegistration?: string;
  contingencyJustification?: string;
  operationIndicatorCode?: string;
  operationType?: InvoiceOperationType;
  // Tax reform
  deliveryPrevisionDate?: Moment;
  occurrenceCityIbsCbs?: string;
  governmentType?: InvoiceGovernmentType;
  governmentReducer?: number;
  governmentOperation?: InvoiceGovernmentOperation;
  taxationNature?: InvoiceTaxationNature;
}

export interface InvoiceNFSeFormValues {
  // TODO: Refatorar as props para o formato da API e RENOMAR AS INTERFACES DE TIPOS PARA OS NOMES CORRETOS
  serie: string;
  ufCode: string;
  cityId: number;
  cityName: string;
  companyId: number;
  insurerId?: number;
  customerId?: number;
  supplierId?: number;
  emissionDate?: string;
  taxRegime?: TaxRegime;
  natureOfOperation?: number;
  recipientType?: RecipientType;
  model: string;
  simpleNational: string;
  culturalIncentive: string;
  issWithheld: string;
  deductIss: string;
  pisCofinsWithholding: string;
  // ----------NÂO USADOS--------------
  numberRps?: string;
  municipalTaxCode?: string;
  recipient: InvoiceNFeRecipientFormValues;
  comments?: string;
}

export interface InvoiceTaxes {
  baseCofins: number;
  baseIss: number;
  basePis: number;
  deliveryDeductionIcmsSt: true;
  deliveryDeductionIpi: true;
  id: number;
  issDeduction: true;
  pisCofinsWithheld: true;
  quotaCofins: number;
  quotaCsll: number;
  quotaInss: number;
  quotaIrpj: number;
  quotaIss: number;
  quotaPis: number;
  valueCofins: number;
  valueCofinsSt: number;
  valueCofinsWithheld: number;
  valueCsll: number;
  valueDeduction: number;
  valueFcp: number;
  valueFcpSt: number;
  valueFcpStWithheld: number;
  valueIcms: number;
  valueIcmsSt: number;
  valueInss: number;
  valueIpi: number;
  valueIrpj: number;
  valueIss: number;
  valuePis: number;
  valuePisSt: number;
  valuePisWithheld: number;
}

export interface InvoiceItem {
  cest?: string;
  cfopId?: number;
  codif?: string;
  consumableId?: number;
  description: string;
  itemTaxes?: {
    additionalValueMarginSt: number;
    baseCofins: number;
    baseCofinsSt: number;
    baseCsll: number;
    baseFcp: number;
    baseFcpSt: number;
    baseIcms: number;
    baseIcmsSt: number;
    baseIcmsStWithheld: number;
    baseInterstateIcms: number;
    baseIpi: number;
    baseIssqn: number;
    basePis: number;
    basePisSt: number;
    calcPercentCofins: true;
    calcPercentCofinsSt: true;
    calcPercentPis: true;
    calcPercentPisSt: true;
    csosnIcms: string;
    cstCofins: string;
    cstIcms: string;
    cstIpi: string;
    cstPis: string;
    deliveryValue: number;
    expenseValue: number;
    id: number;
    insuranceValue: number;
    modBcIcms: number;
    modBcIcmsSt: number;
    motDesIcms: number;
    originIcms: number;
    percentFcp: number;
    percentProvisionalParticipation: number;
    quotaBcOp: number;
    quotaCofins: number;
    quotaCofinsSt: number;
    quotaCsll: number;
    quotaDif: number;
    quotaFcp: number;
    quotaFcpSt: number;
    quotaIcms: number;
    quotaIcmsSt: number;
    quotaIcmsUfDestination: number;
    quotaInterstateIcms: number;
    quotaIpi: number;
    quotaIrpj: number;
    quotaIssqn: number;
    quotaMvaSt: number;
    quotaPis: number;
    quotaPisSt: number;
    reductionIcms: number;
    reductionIcmsSt: number;
    taxRegime: InvoiceTaxRegime;
    ufSt: string;
    valueCofins: number;
    valueCofinsSt: number;
    valueCsll: number;
    valueFcp: number;
    valueFcpSt: number;
    valueIcms: number;
    valueIcmsDif: number;
    valueIcmsFcp: number;
    valueIcmsOp: number;
    valueIcmsSt: number;
    valueIcmsStWithheld: number;
    valueIcmsUnencumbered: number;
    valueInterstateDestinationIcms: number;
    valueInterstateOriginIcms: number;
    valueIpi: number;
    valueIrpj: number;
    valueIssqn: number;
    valuePis: number;
    valuePisSt: number;
    valueTotal: number;
    valueUnityCofins: number;
    valueUnityCofinsSt: number;
    valueUnityPis: number;
    valueUnityPisSt: number;
  };
  ncmId?: number;
  origin?: string;
  partId: number;
  productCodeAnp?: string;
  quantity: number;
  taskId?: number;
  valueDiscount?: number;
  valueUnitary: number;
}

export interface InvoiceListParams extends PageRequest {
  companyId?: number;
  customerId?: number;
  emissionDate?: string;
  entity?: InvoiceOriginReferenceEntity;
  entityCode?: string;
  entityId?: number;
  insurerId?: number;
  invoiceType?: InvoiceType;
  model?: string;
  number?: number;
  serie?: string;
  status?: string;
  supplierId?: number;
  active?: boolean;
}

export interface Transport {
  brand: string;
  carrierId: number;
  grossWeight: number;
  id: number;
  netWeight: number;
  number: string;
  plate: string;
  rntc: string;
  shippingMode: 'CONTRACTING_DELIVERY_BY_THE_SENDER';
  state: string;
  volumeQuantity: number;
  volumeType: string;
}

export interface NfeInfo {
  id: number;
  key: string;
  message: string;
  protocol: string;
  reason: string;
  receiptTime: string;
  situation: 'REPLACED';
}

export interface DeliverPlace {
  cityId: number;
  complement: string;
  cpfCnpj: string;
  district: string;
  id: number;
  number: string;
  street: string;
  zip: string;
}

export interface InvoiceDTO {
  batchNumber: string;
  companyId: number;
  customerId: number;
  deliveryType: DeliveryType;
  departureDate: string;
  documentType: DocumentType;
  emissionDate: string;
  emissionGoal: InvoiceEmissionGoal;
  debitType?: InvoiceDebitNoteType;
  creditType?: InvoiceCreditNoteType;
  id: number;
  insurerId: number;
  invoiceItems: InvoiceItem[];
  invoiceTaxes: InvoiceTaxes;
  invoiceType: InvoiceType;
  key: string;
  model: string;
  nacionalSimpleOptant: number;
  number: string;
  operationDestination: OperationDestination;
  presenceIndication: PresenceIndications;
  serie: string;
  situation: InvoiceSituation;
  status: string;
  supplierId: number;
  taxPayerType: InvoiceTaxpayerType;
  taxRegime: InvoiceTaxRegime;
  ufCode: number;
  xmlPath: string;
  customerName?: string;
  supplierName?: string;
  insurerName?: string;
  originReference?: InvoiceOriginReference[];
  isInternalApiIssued: boolean;
}

export interface InvoiceColumns {
  serie?: string;
  emissionDate?: string;
  customerName?: string;
  supplierName?: string;
  insurerName?: string;
  id?: number;
  invoiceType?: InvoiceType;
  number?: string;
  situation?: InvoiceSituation;
  isInternalApiIssued: boolean;
}

// -> Create \/

export interface InvoiceCreateRequest {
  number?: number;
  companyId?: number;
  customerId?: number;
  insurerId?: number;
  deliveryType?: DeliveryType;
  departureDate?: string;
  documentType?: DocumentType;
  emissionDate?: string;
  emissionType?: EmissionType;
  emissionGoal?: InvoiceEmissionGoal;
  debitType?: InvoiceDebitNoteType;
  creditType?: InvoiceCreditNoteType;
  invoiceItems?: InvoiceItem[];
  invoiceTaxes?: InvoiceTaxes;
  model?: string;
  nacionalSimpleOptant?: number;
  operationDestination?: OperationDestination;
  presenceIndication?: PresenceIndications;
  serie?: string;
  supplierId?: number;
  taxpayerType?: InvoiceTaxpayerType;
  taxRegime?: InvoiceTaxRegime;
  ufCode?: string;
  intermediaryIndication?: boolean;
  invoiceType?: InvoiceType;
  cityId?: number;
  cityName?: string;
  key?: string;
  natureOfOperation?: string;
  finalConsumer?: boolean;
  recipientType?: RecipientType;
  //---------------------------
  simpleNational?: boolean;
  culturalIncentive?: boolean;
  issWithheld?: boolean;
  deductIss?: boolean;
  pisCofinsWithholding?: boolean;
  saleId?: number;
  // Tax Reform
  deliveryPrevisionDate?: string;
  occurrenceCityIbsCbs?: string;
  governmentType?: InvoiceGovernmentType;
  governmentReducer?: number;
  governmentOperation?: InvoiceGovernmentOperation;
}

export interface InvoiceCreateDTO {
  batchNumber: string;
  companyId: number;
  customerId: number;
  deleted?: boolean;
  deliveryType: DeliveryType;
  departureDate: string;
  documentType: InvoiceDocumentType;
  emissionDate: string;
  emissionGoal: InvoiceEmissionGoal;
  debitType?: InvoiceDebitNoteType;
  creditType?: InvoiceCreditNoteType;
  id: number;
  insurerId: number;
  invoiceItems: InvoiceItem[];
  invoiceTaxes: InvoiceTaxes;
  key: string;
  model: string;
  nacionalSimpleOptant: number;
  number: string;
  originReference: InvoiceOriginReference[];
  operationDestination: InvoiceOperationDestination;
  presenceIndication: PresenceIndications;
  serie: string;
  situation: InvoiceSituation;
  status: string;
  supplierId: number;
  taxpayerType: InvoiceTaxpayerType;
  taxRegime: InvoiceTaxRegime;
  ufCode: number;
  xmlPath: string;
  intermediaryIndication: boolean;
  includeIcms: boolean;
  includeIpi: boolean;
  emissionType: InvoiceEmissionType;
  finalConsumer: boolean;
  natureOfOperation: string;
  recipientType?: InvoiceRecipientType;
  cityId?: number;
  invoiceType?: InvoiceType;
  isInternalApiIssued?: boolean;
  retentionPisCofins?: boolean;
  //---------------------------
  simpleNational?: string;
  culturalIncentive?: string;
  culturalIncentivator?: string;
  issWithheld?: string;
  deductIss?: string;
  pisCofinsWithholding?: string;
  //---------------------------
  taxAdditionalData: string;
  companyAdditionalData: string;
  faceToFace: boolean;
  intermediaryCpfCnpj: string;
  registrationIdentifier: string;
  description?: string;
  numberRps?: string;
  integrationId: string;
  plugNotasId?: string;
  protocol?: string;
  cancellationProtocol?: string;
  intermediaryCityCode: string;
  intermediaryCompanyName: string;
  intermediaryMunicipalRegistration: string;
  serviceItemList?: string;
  taxCode?: string;
  taxationNature?: InvoiceTaxationNature;
  fantasyName?: string;
  commitmentNote?: string;
  orderBuy?: string;
  contract?: string;
  publicAgency?: string;
  // Tax reform ---------------
  deliveryPrevisionDate?: string;
  occurrenceCityIbsCbs?: string;
  governmentType?: InvoiceGovernmentType;
  governmentReducer?: number;
  governmentOperation?: InvoiceGovernmentOperation;
}

export interface DataInvoiceUpdate {
  companyId?: number;
  model?: string;
  serie?: string;
  number?: number;
  key?: string;
  natureOfOperation: string;
  emissionGoal: InvoiceEmissionGoal;
  debitType?: InvoiceDebitNoteType;
  creditType?: InvoiceCreditNoteType;
  situation: InvoiceSituation;
  emissionDate?: string;
  documentType?: InvoiceDocumentType;
  finalConsumer: boolean;
  operationDestination: InvoiceOperationDestination;
  departureDate?: string;
  emissionType: InvoiceEmissionType;
  presenceIndication: PresenceIndications;
  ufCode: string;
  cityId?: number;
  intermediaryIndication: boolean;
  includeIcms: boolean;
  includeIpi: boolean;
  invoiceType: InvoiceType;
  customerId?: number;
  supplierId?: number;
  insurerId?: number;
  id: number;
  taxpayerType?: InvoiceTaxpayerType;
  recipientType?: InvoiceRecipientType;
  // Tax Reform
  deliveryPrevisionDate?: string;
  occurrenceCityIbsCbs?: string;
  governmentType?: InvoiceGovernmentType;
  governmentReducer?: number;
  governmentOperation?: InvoiceGovernmentOperation;
}

// Botões

export interface InvoiceConsultTecnoSpeedRef {
  consultInvoice: () => Promise<void>;
}

export enum DocTypeEnum {
  xml = 0,
  pdf = 1,
  view = 2,
}

export enum ConsultInvoiceStatus {
  rejeitado = 'REJEITADO',
  concluido = 'CONCLUIDO',
  denegado = 'DENEGADO',
  cancelado = 'CANCELADO',
  processando = 'PROCESSANDO',
}

export enum CancelInvoiceCode {
  serviceNotProvided,
  IssuedInDuplicate,
  incorrectCpfCnpj,
}

export interface CancelInvoiceRequest {
  invoiceId: number;
  justification: string;
  code?: CancelInvoiceCode;
}

export interface SendEmailRequest {
  invoiceId: number;
  resendToRecipient: boolean;
  otherRecipients: string[];
}

export interface ConsultInvoiceResponse {
  id: string;
  integrationId: string;
  emissionDate: string;
  message: string;
  cStat: number;
  invoiceSeries: string;
  cancellationDate: string;
  cancellationProtocol: string;
  key: string;
  pdf: string;
  xml: string;
  number: string;
  status: ConsultInvoiceStatus;
  issuer: string;
  recipient: string;
  valor: number;
  authorizationDate: string;
  protocol: string;
  numberRps: number;
}

export interface CancelConsultInvoiceResponse {
  message: string;
  data: {
    status: ConsultInvoiceStatus;
    justification: string;
    response?: string;
    inProcess: boolean;
  };
}

export enum DownloadXmlInvoiceAction {
  Download = 'Download',
  Display = 'Display',
}

export interface DownloadXmlInvoiceRequest {
  invoiceId: number;
  docType: number;
  action?: DownloadXmlInvoiceAction;
}

// Dados Adicionais

export interface UpdateInvoiceAdditionalDTO {
  invoiceId: number;
  taxAdditionalData: string;
  companyAdditionalData: string;
  commitmentNote?: string;
  orderBuy?: string;
  contract?: string;
}

export interface DownloadXmlInvoiceResponseMessage {
  message: 'Documento em processamento, aguarde alguns minutos e tente novamente.';
}

export interface InvoiceTefPaymentAvailabilityResponse {
  canShow: boolean;
}

export interface InvoiceSpedListRequest {
  companyId: number;
  invoiceIds?: number[];
  invoiceTypes?: Exclude<InvoiceType, 'CFE'>[];
  month: string;
  number?: string;
  recipientType?: InvoiceRecipientType;
  year: string;
}

export interface InvoiceSped {
  id: number;
  invoiceType: Exclude<InvoiceType, 'CFE'>;
  key: string;
  number: string;
  recipientName: string;
  recipientType: InvoiceRecipientType;
  statusRegisteredInPlugStorage: InvoiceSpedStatusRegistered;
}
